import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import {
  createCreditApplication,
  createManualUploadCreditApplication,
  getCreditApplications,
  getCreditApplication,
  getCreditApplicationPDF,
  updateCreditApplication,
  getSupplierCreditApplication,
  sendReferencesCreditApplication,
  makeCreditAppSignature,
  updateSupplierCreditApplication,
  editCreditApplicationData,
  sendFeedbackCreditApplication,
  resolveFeedbackCreditApplication,
  decideTaxExemptReview,
  applyTaxExemptNotExempt,
  sendCreditApplicationTemplateLink,
  resendRequestCreditAppSignature,
  requestSignatureDetailsUpdate,
  requestUpdateCreditApplication,
  createApplicationUpdateHistory,
  updateApplicationUpdateHistory,
  getApplicationHistory,
  refreshPlaidReportApplication,
  internalApproveCreditApplication,
  updateBankReferenceResponse,
  sendBankReference,
} from '../services/creditApplicationsService';
import { CreditApplicationsInitialState } from '../globalSlices/creditApplicationsSlice';
import { CreditApplication } from '@dill/dill-shared/dist/src';

export const createCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'createCreditApplication';
  builder.addCase(createCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createCreditApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(createCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'getCreditApplication';
  builder.addCase(getCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.creditApplication = null;
  });
  builder.addCase(getCreditApplication.fulfilled, (state, action: PayloadAction<any | null>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if (action.payload && action.payload.success && action.payload.data) {
      state.creditApplication = action.payload.data;
    }
  });
  builder.addCase(getCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getCreditApplicationsExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'getCreditApplications';
  builder.addCase(getCreditApplications.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(getCreditApplications.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if (!action.payload) return;
    const { success, data } = action.payload;
    if (success && data) {
      if (action.meta?.arg?.isArchived) {
        state.archivedCreditApplications = data;
      } else {
        state.creditApplications = data;
      }
    }
  });
  builder.addCase(getCreditApplications.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'updateCreditApplication';
  builder.addCase(updateCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(updateCreditApplication.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if (action.payload?.success && action.payload.data) {
      const data = action.payload.data as CreditApplication;

      // Background/side-effect calls (e.g. the auto SENT->OPENED status ping) set this so their
      // response — a DB snapshot that predates whatever the applicant has typed locally since —
      // doesn't get merged into state.creditApplication and re-trigger the form's re-sync
      // effects with stale fieldValues. See the flag's definition in creditApplicationsService.
      const skipCreditApplicationSync = action.meta.arg?.skipCreditApplicationSync;

      if (!skipCreditApplicationSync) {
        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
      }
      state.creditApplications = state.creditApplications.map((item) =>
        item.id === data.id ? data : item
      );

      state.archivedCreditApplications = state.archivedCreditApplications.map((item) =>
        item.id === data.id ? data : item
      );
    }
  });
  builder.addCase(updateCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getSupplierCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'getSupplierCreditApplication';
  builder.addCase(getSupplierCreditApplication.pending, (state, action) => {
    if (action.meta?.arg?.skipLoading !== 'true') {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
      state.creditApplication = null;
    }
  });
  builder.addCase(
    getSupplierCreditApplication.fulfilled,
    (state, action: PayloadAction<any | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action.payload && action.payload.success && action.payload.data) {
        state.creditApplication = action.payload.data;
      }
    }
  );
  builder.addCase(getSupplierCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getCreditApplicationPDFExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'getCreditApplicationPDF';
  builder.addCase(getCreditApplicationPDF.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(getCreditApplicationPDF.fulfilled, (state, action: PayloadAction<any | null>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
  builder.addCase(getCreditApplicationPDF.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const sendReferencesCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'sendReferencesCreditApplication';
  builder.addCase(sendReferencesCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    sendReferencesCreditApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      if (action && action?.payload?.success && action.payload.data) {
        state.creditApplication = { ...(state.creditApplication ?? {}), ...action.payload.data };
      }
    }
  );

  builder.addCase(sendReferencesCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateSupplierCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'updateSupplierCreditApplication';

  builder.addCase(updateSupplierCreditApplication.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    updateSupplierCreditApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);

      if (action.payload?.success && action.payload.data) {
        const data = action.payload.data as CreditApplication;

        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
        state.creditApplications = state.creditApplications.map((item) =>
          item.id === data.id ? data : item
        );

        state.archivedCreditApplications = state.archivedCreditApplications.map((item) =>
          item.id === data.id ? data : item
        );
      }
    }
  );

  builder.addCase(updateSupplierCreditApplication.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const editCreditApplicationDataExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'editCreditApplicationData';

  builder.addCase(editCreditApplicationData.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    editCreditApplicationData.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);

      if (action.payload?.success && action.payload.data) {
        const data = action.payload.data as CreditApplication;

        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
        state.creditApplications = state.creditApplications.map((item) =>
          item.id === data.id ? data : item
        );

        state.archivedCreditApplications = state.archivedCreditApplications.map((item) =>
          item.id === data.id ? data : item
        );
      }
    }
  );

  builder.addCase(editCreditApplicationData.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createManualUploadCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'createManualUploadCreditApplication';
  builder.addCase(createManualUploadCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createManualUploadCreditApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(
    createManualUploadCreditApplication.rejected,
    (state, action: PayloadAction<any>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
};

// Feedback endpoints return the updated feedback fields and history. Patch them in place so the
// details page updates without refetching (and showing the full-page loader). When the feedback
// is on an update-history record, patch that record instead of the application itself.
const applyFeedbackUpdate = (
  state: CreditApplicationsInitialState,
  data: Partial<CreditApplication>,
  applicationHistoryId?: string | null
) => {
  if (!state.creditApplication) return;
  if (applicationHistoryId) {
    state.creditApplication = {
      ...state.creditApplication,
      applicationUpdateHistory: (state.creditApplication.applicationUpdateHistory ?? []).map(
        (history) => (history.id === applicationHistoryId ? { ...history, ...data } : history)
      ),
    };
    return;
  }
  state.creditApplication = { ...state.creditApplication, ...data };
};

export const sendFeedbackCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'sendFeedbackCreditApplication';
  builder.addCase(sendFeedbackCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(sendFeedbackCreditApplication.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if (action.payload?.success && action.payload.data) {
      applyFeedbackUpdate(state, action.payload.data, action.meta.arg.applicationHistoryId);
    }
  });
  builder.addCase(sendFeedbackCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

// Tax exemption review endpoints return the fields they changed; patch the open application.
export const taxExemptReviewExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  [decideTaxExemptReview, applyTaxExemptNotExempt].forEach((thunk) => {
    const reducerId = thunk.typePrefix.split('/')[1];
    builder.addCase(thunk.pending, (state) => {
      state.loadingList = [...new Set([...state.loadingList, reducerId])];
    });
    builder.addCase(thunk.fulfilled, (state, action) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
      const data = action.payload?.data;
      if (
        action.payload?.success &&
        data &&
        state.creditApplication?.id === action.meta.arg.creditApplicationId
      ) {
        const { tasks, ...creditApplicationChanges } = data;
        state.creditApplication = { ...state.creditApplication, ...creditApplicationChanges };
      }
    });
    builder.addCase(thunk.rejected, (state) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    });
  });
};

export const resolveFeedbackCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'resolveFeedbackCreditApplication';
  builder.addCase(resolveFeedbackCreditApplication.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(resolveFeedbackCreditApplication.fulfilled, (state, action) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if (action.payload?.success && action.payload.data) {
      applyFeedbackUpdate(state, action.payload.data, action.meta.arg.applicationHistoryId);
    }
  });
  builder.addCase(resolveFeedbackCreditApplication.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const sendCreditApplicationTemplateLinkExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'sendCreditApplicationTemplateLink';
  builder.addCase(sendCreditApplicationTemplateLink.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    sendCreditApplicationTemplateLink.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(
    sendCreditApplicationTemplateLink.rejected,
    (state, action: PayloadAction<any>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
};

export const requestUpdateCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'requestUpdateCreditApplication';
  builder.addCase(requestUpdateCreditApplication.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    requestUpdateCreditApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(requestUpdateCreditApplication.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const createApplicationUpdateHistoryExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'createApplicationUpdateHistory';
  builder.addCase(createApplicationUpdateHistory.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    createApplicationUpdateHistory.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(createApplicationUpdateHistory.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateApplicationUpdateHistoryExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'updateApplicationUpdateHistory';

  builder.addCase(updateApplicationUpdateHistory.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    updateApplicationUpdateHistory.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);

      if (action.payload?.success && action.payload.data) {
        const data = action.payload.data as CreditApplication;

        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
        state.creditApplications = state.creditApplications.map((item) =>
          item.id === data.id ? data : item
        );
      }
    }
  );

  builder.addCase(updateApplicationUpdateHistory.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const getApplicationHistoryExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'getApplicationHistory';
  builder.addCase(getApplicationHistory.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
    state.creditApplication = null;
  });
  builder.addCase(getApplicationHistory.fulfilled, (state, action: PayloadAction<any | null>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    if (action.payload && action.payload.success && action.payload.data) {
      state.creditApplication = action.payload.data;
    }
  });
  builder.addCase(getApplicationHistory.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const resendRequestCreditAppSignatureExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'resendRequestCreditAppSignature';
  builder.addCase(resendRequestCreditAppSignature.pending, (state, action) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    resendRequestCreditAppSignature.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(resendRequestCreditAppSignature.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const requestSignatureDetailsUpdateExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'requestSignatureDetailsUpdate';
  builder.addCase(requestSignatureDetailsUpdate.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });
  builder.addCase(
    requestSignatureDetailsUpdate.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );
  builder.addCase(requestSignatureDetailsUpdate.rejected, (state, action: PayloadAction<any>) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const makeCreditAppSignatureExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'makeCreditAppSignature';

  builder.addCase(makeCreditAppSignature.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    makeCreditAppSignature.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);

      if (action.payload?.success && action.payload.data) {
        const data = action.payload.data as CreditApplication;

        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
        state.creditApplications = state.creditApplications.map((item) =>
          item.id === data.id ? data : item
        );
      }
    }
  );

  builder.addCase(makeCreditAppSignature.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const refreshPlaidReportApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'refreshPlaidReportApplication';

  builder.addCase(refreshPlaidReportApplication.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    refreshPlaidReportApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);

      if (action.payload?.success && action.payload.data) {
        const data = action.payload.data as CreditApplication;

        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
        state.creditApplications = state.creditApplications.map((item) =>
          item.id === data.id ? data : item
        );
      }
    }
  );

  builder.addCase(refreshPlaidReportApplication.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const internalApproveCreditApplicationExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'internalApproveCreditApplication';

  builder.addCase(internalApproveCreditApplication.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    internalApproveCreditApplication.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);

      if (action.payload?.success && action.payload.data) {
        const data = action.payload.data as CreditApplication;

        state.creditApplication = { ...(state.creditApplication ?? {}), ...data };
        state.creditApplications = state.creditApplications.map((item) =>
          item.id === data.id ? data : item
        );

        state.archivedCreditApplications = state.archivedCreditApplications.map((item) =>
          item.id === data.id ? data : item
        );
      }
    }
  );

  builder.addCase(internalApproveCreditApplication.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const updateBankReferenceResponseExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'updateBankReferenceResponse';

  builder.addCase(updateBankReferenceResponse.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    updateBankReferenceResponse.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );

  builder.addCase(updateBankReferenceResponse.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};

export const sendBankReferenceExtraReducer = (
  builder: ActionReducerMapBuilder<CreditApplicationsInitialState>
) => {
  const reducerId = 'sendBankReference';

  builder.addCase(sendBankReference.pending, (state) => {
    state.loadingList = [...new Set([...state.loadingList, reducerId])];
  });

  builder.addCase(
    sendBankReference.fulfilled,
    (state, action: PayloadAction<AppServerResponse | null>) => {
      state.loadingList = state.loadingList.filter((item) => item !== reducerId);
    }
  );

  builder.addCase(sendBankReference.rejected, (state) => {
    state.loadingList = state.loadingList.filter((item) => item !== reducerId);
  });
};
